<template>
  <div class="ec-list">

    <admin-table-subheader
      v-model="search"
      title="Resellers"
      search
      search-placeholder="Search partners…"
      :refresh-button="true"
      :refresh-loading="loading"
      :primary-button="{ label: 'New Reseller', icon: 'mdi-plus', click: opennew }"
      @refresh="initialize"
    />

    <div v-if="loading" class="ec-loading">
      <v-progress-circular indeterminate color="primary" size="32" />
    </div>

    <v-data-table
      v-else
      :headers="headers"
      :items="resellers"
      :search="search"
      :items-per-page="30"
      :footer-props="{ 'items-per-page-options': [10, 30, 50, -1] }"
      class="ec-table"
    >
      <template v-slot:[`item.name`]="{ item }">
        <strong>{{ item.name }}</strong>
      </template>

      <template v-slot:[`item.slug`]="{ item }">
        <code class="rs-slug">{{ item.slug }}</code>
      </template>

      <template v-slot:[`item.commission_rate`]="{ item }">
        <span class="u-date">%{{ Math.round(Number(item.commission_rate) * 100) }}</span>
      </template>

      <template v-slot:[`item.is_active`]="{ item }">
        <span class="u-status-pill" :class="item.is_active ? 'u-pill--green' : 'u-pill--red'">
          <v-icon size="11" style="margin-right:3px">{{ item.is_active ? 'mdi-check' : 'mdi-close' }}</v-icon>
          {{ item.is_active ? 'Active' : 'Inactive' }}
        </span>
      </template>

      <template v-slot:[`item.created_at`]="{ item }">
        <span class="u-date">{{ fmtDate(item.created_at) }}</span>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <button class="ec-icon-btn ec-icon-btn--primary" title="Pools & Report" @click="manage(item)">
          <v-icon size="15">mdi-view-dashboard-outline</v-icon>
        </button>
        <button class="ec-icon-btn" title="Edit" @click="edit(item)">
          <v-icon size="15">mdi-pencil-outline</v-icon>
        </button>
        <button class="ec-icon-btn ec-icon-btn--danger" title="Delete" @click="askDelete(item)">
          <v-icon size="15">mdi-delete-outline</v-icon>
        </button>
      </template>
    </v-data-table>

    <reseller-form
      v-if="showForm"
      :show="showForm"
      :reseller="editing"
      @close="showForm = false"
      @saved="onSaved"
      @notify="notify"
    />

    <reseller-detail
      v-if="showDetail"
      :show="showDetail"
      :reseller="selected"
      @close="showDetail = false"
    />

    <confirm-dialog
      :show.sync="confirm.show"
      :title="confirm.title"
      :message="confirm.message"
      :item-name="confirm.itemName"
      :loading="confirm.loading"
      confirm-text="Delete"
      cancel-text="Cancel"
      type="danger"
      @confirm="doDelete"
    />

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Close</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { apiService } from '../../service/api.service'
import AdminTableSubheader from '../shared/AdminTableSubheader.vue'
import ConfirmDialog from '../shared/ConfirmDialog.vue'
import ResellerForm from './ResellerForm.vue'
import ResellerDetail from './ResellerDetail.vue'

export default {
  name: 'ResellerTable',
  components: { AdminTableSubheader, ConfirmDialog, ResellerForm, ResellerDetail },
  data () {
    return {
      resellers: [],
      loading: false,
      search: null,
      showForm: false,
      showDetail: false,
      editing: null,
      selected: null,
      confirm: { show: false, title: '', message: '', itemName: '', loading: false, target: null },
      snackbar: { show: false, text: '', color: 'success' },
      headers: [
        { text: 'Name', value: 'name', align: 'start' },
        { text: 'Slug', value: 'slug', align: 'start' },
        { text: 'Commission', value: 'commission_rate', align: 'center' },
        { text: 'Billing', value: 'billing_model', align: 'center' },
        { text: 'Active', value: 'is_active', align: 'center' },
        { text: 'Created', value: 'created_at', align: 'center' },
        { text: 'Actions', value: 'actions', align: 'center', sortable: false }
      ]
    }
  },
  mounted () {
    this.initialize()
  },
  methods: {
    notify (text, color = 'success') {
      this.snackbar = { show: true, text, color }
    },
    fmtDate (v) {
      if (!v) return '—'
      const d = new Date(v)
      return isNaN(d) ? v : d.toLocaleDateString()
    },
    async initialize () {
      this.loading = true
      await apiService.get('admin/resellers')
        .then((res) => { this.resellers = res.data || [] })
        .catch(() => this.notify('Failed to load partners', 'error'))
        .finally(() => (this.loading = false))
    },
    opennew () {
      this.editing = null
      this.showForm = true
    },
    edit (item) {
      this.editing = { ...item }
      this.showForm = true
    },
    manage (item) {
      this.selected = { ...item }
      this.showDetail = true
    },
    onSaved () {
      this.showForm = false
      this.initialize()
      this.notify('Partner saved')
    },
    askDelete (item) {
      this.confirm = {
        show: true,
        title: 'Delete partner',
        message: 'This partner will be deleted:',
        itemName: item.name,
        loading: false,
        target: item
      }
    },
    async doDelete () {
      this.confirm.loading = true
      await apiService.delete(`admin/resellers/${this.confirm.target.id}`)
        .then(() => { this.notify('Partner deleted'); this.initialize() })
        .catch((err) => this.notify(err.response?.data?.message || 'Operation failed', 'error'))
        .finally(() => { this.confirm.show = false; this.confirm.loading = false })
    }
  }
}
</script>

<style scoped>
.rs-slug {
  font-size: .78rem;
  font-weight: 600;
  color: var(--ec-text-1);
  background: var(--ec-gray-bg);
  padding: 2px 7px;
  border-radius: 4px;
}
</style>
